<template>
  <section id="splash" class="splash-section">
    <div class="splash-background">
      <div class="design-anchor edge-top-left">
        <img :src="greenCornerBackgroundElement" alt="" @error="applySplashFallback('green')" />
      </div>
      <div class="design-anchor edge-top-center-left">
        <img :src="purpleCornerBackgroundElement" alt="" @error="applySplashFallback('purple')" />
      </div>
      <div class="design-anchor edge-top-center-right">
        <img :src="orangeCornerBackgroundElement" alt="" @error="applySplashFallback('orange')" />
      </div>
      <div class="design-anchor edge-top-right">
        <img :src="greenCornerBackgroundElement" alt="" @error="applySplashFallback('green')" />
      </div>
      <div class="design-anchor edge-right-mid">
        <img :src="orangeCornerBackgroundElement" alt="" @error="applySplashFallback('orange')" />
      </div>
      <div class="design-anchor edge-bottom-right">
        <img :src="blueCornerBackgroundElement" alt="" @error="applySplashFallback('blue')" />
      </div>
      <div class="design-anchor edge-bottom-left">
        <img :src="purpleCornerBackgroundElement" alt="" @error="applySplashFallback('purple')" />
      </div>
      <div class="design-anchor edge-left-mid">
        <img :src="greenCornerBackgroundElement" alt="" @error="applySplashFallback('green')" />
      </div>
    </div>
    <div class="splash-content">
      <div class="splash-left-content">
        <div class="splash-copy-panel">
          <h1 class="splash-title">
            <span class="title-main">Welcome to<br />Campaign Creator</span>
          </h1>
          <p class="splash-subtitle">Your AI enabled Gamification Platform</p>
          <p class="splash-description">
            You're about to create your own fully branded marketing campaign — no coding required.
            Just follow the simple step by step prompts to create your campaign.
            When you have completed your campaign build, your campaign URL will be emailed directly to you.
            You can then use this link to promote your campaign across your marketing communications.
          </p>
        </div>
        <div class="splash-cta">
          <button type="button" class="how-it-works-button" @click="emitHowItWorks">
            How to Use
          </button>
          <button type="button" class="start-button" @click.prevent="emitGetStarted" :disabled="isDisabled">
            Get Started
          </button>
        </div>

        <div class="case-study-cta">
          <button type="button" class="case-study-card" aria-label="Open use cases" @click="emitCaseStudies">
            <img :src="caseStudiesImage" alt="" class="case-study-img" />
          </button>
          <p class="case-study-label-text">Use Case Suggestions</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { splashBackground, platformIcon } from '@/config/platformAssets'

const splashBackgroundSources = {
  blue: {
    primary: splashBackground('splash-blue.png'),
    fallback: splashBackground('blue-top.png'),
  },
  green: {
    primary: splashBackground('splash-green.png'),
    fallback: splashBackground('green-left.png'),
  },
  orange: {
    primary: splashBackground('spalsh-oranage.png'),
    fallback: splashBackground('orange-bottom.png'),
  },
  purple: {
    primary: splashBackground('splash-purple.png'),
    fallback: splashBackground('purple-top.png'),
  },
} as const

const blueCornerBackgroundElement = ref(splashBackgroundSources.blue.primary)
const greenCornerBackgroundElement = ref(splashBackgroundSources.green.primary)
const orangeCornerBackgroundElement = ref(splashBackgroundSources.orange.primary)
const purpleCornerBackgroundElement = ref(splashBackgroundSources.purple.primary)
const caseStudiesImage = platformIcon('star.png')

const props = defineProps<{
  modelUrl?: string | null
  disabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'get-started'): void
  (e: 'show-how-it-works'): void
  (e: 'show-case-studies'): void
}>()

const isDisabled = computed(() => props.disabled ?? false)

const emitGetStarted = () => {
  if (!isDisabled.value) {
    emit('get-started')
  }
}

const emitHowItWorks = () => {
  emit('show-how-it-works')
}

const emitCaseStudies = () => {
  emit('show-case-studies')
}

const applySplashFallback = (color: keyof typeof splashBackgroundSources) => {
  if (color === 'blue' && blueCornerBackgroundElement.value !== splashBackgroundSources.blue.fallback) {
    blueCornerBackgroundElement.value = splashBackgroundSources.blue.fallback
  }

  if (color === 'green' && greenCornerBackgroundElement.value !== splashBackgroundSources.green.fallback) {
    greenCornerBackgroundElement.value = splashBackgroundSources.green.fallback
  }

  if (color === 'orange' && orangeCornerBackgroundElement.value !== splashBackgroundSources.orange.fallback) {
    orangeCornerBackgroundElement.value = splashBackgroundSources.orange.fallback
  }

  if (color === 'purple' && purpleCornerBackgroundElement.value !== splashBackgroundSources.purple.fallback) {
    purpleCornerBackgroundElement.value = splashBackgroundSources.purple.fallback
  }
}
</script>

<style scoped>
.splash-section {
  width: 100%;
  height: var(--section-available-height);
  min-height: var(--section-available-height);
  max-height: var(--section-available-height);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: safe center;
  background-color: var(--color-background);
  padding: 0 !important;
  padding-bottom: calc(var(--footer-clearance) + 3dvh) !important;
}

.splash-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  max-width: none !important;
  margin-inline: 0 !important;
  pointer-events: none;
  z-index: 0;
}

.design-anchor {
  position: absolute;
  pointer-events: none;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.design-anchor img {
  width: var(--design-anchor-size, 400px);
  height: auto;
  opacity: 0.92;
  filter: brightness(1.05) saturate(1.2) contrast(1.08);
}

.edge-top-left {
  left: 200px;
  top: 100px;
  transform: translate(-35%, -30%);
  --design-anchor-size: clamp(240px, 30vw, 520px);
}

.edge-top-center-left {
  left: 32%;
  top: 0;
  transform: translate(-50%, -40%) rotate(12deg);
  --design-anchor-size: clamp(280px, 32vw, 580px);
}

.edge-top-center-right {
  left: 68%;
  top: 0;
  transform: translate(-50%, -38%) rotate(-8deg);
  --design-anchor-size: clamp(280px, 32vw, 580px);
}

.edge-top-right {
  right: 200px;
  top: 100px;
  transform: translate(30%, -28%) scaleX(-1);
  --design-anchor-size: clamp(300px, 34vw, 600px);
}

.edge-right-mid {
  right: 0;
  top: 50%;
  transform: translate(35%, -50%) rotate(-15deg);
  --design-anchor-size: clamp(220px, 28vw, 480px);
}

.edge-bottom-right {
  right: 150px;
  bottom: 0;
  transform: translate(30%, 32%) scaleY(-1);
  --design-anchor-size: clamp(260px, 30vw, 520px);
}

.edge-bottom-left {
  left: 150px;
  bottom: 0;
  transform: translate(-32%, 35%) rotate(8deg);
  --design-anchor-size: clamp(320px, 36vw, 650px);
}

.edge-left-mid {
  left: 0;
  top: 50%;
  transform: translate(-38%, -50%) rotate(10deg);
  --design-anchor-size: clamp(240px, 26vw, 460px);
}

.splash-content {
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: none !important;
  margin-inline: 0 !important;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--base-layout-grid-gap-md);
  padding: 0 clamp(1.5rem, 4vw, 4rem);
}

.splash-left-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: var(--base-layout-section-content-gap);
  width: 100%;
  max-width: min(54rem, 100%);
  margin-inline: auto;
}

.splash-model-container {
  flex: 1;
  width: 100%;
  max-width: 100%;
  height: var(--base-layout-splash-model-container-height);
  min-height: var(--base-layout-splash-model-container-min-height);
  display: flex;
  align-items: center;
  justify-content: center;
}

.splash-copy-panel {
  position: relative;
  width: 100%;
  max-width: min(54rem, 100%);
  padding: 1.65rem 1.82rem 1.82rem;
  border-radius: 1.25rem;
  margin-top: 0;
  background-color: var(--ui-inverse-surface);
  background-image: none;
  opacity: 1;
  border: 2px solid rgba(24, 27, 47, 0.14);
  box-shadow:
    0 22px 48px rgba(7, 10, 20, 0.18),
    0 0 0 1px rgba(255, 255, 255, 0.16) inset;
  font-family: 'Montserrat', sans-serif;
}

.splash-title {
  font-size: var(--base-layout-section-heading-h3-size);
  font-weight: 900;
  margin-bottom: 0;
  line-height: var(--base-layout-section-heading-h3-line-height);
  display: flex;
  flex-direction: column;
  gap: 0;
  text-align: center;
  align-items: center;
}

.title-subheading {
  font-size: clamp(1rem, 2vw, 1.25rem);
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: var(--base-layout-section-header-margin-bottom);
  line-height: var(--base-layout-section-description-line-height);
  font-weight: 400;
  text-align: center;
  max-width: var(--base-layout-section-max-width);
}

.title-main {
  color: #ffffff;
  font-size: clamp(1.75rem, 3vw, 2.9rem);
  font-family: 'Montserrat', sans-serif;
  font-weight: 900;
}

.title-highlight {
  color: var(--vt-c-accent);
  font-size: clamp(1.75rem, 3vw, 2.9rem);
}

.splash-subtitle {
  font-size: clamp(1rem, 2.3vw, 1.35rem);
  color: #ffffff;
  margin: 0.9rem 0 0;
  line-height: 1.15;
  font-weight: 500;
  text-align: center;
  max-width: var(--base-layout-section-max-width);
  font-family: 'Montserrat', sans-serif;
}

.splash-description {
  font-size: clamp(0.95rem, 2vw, 1.15rem);
  color: #ffffff;
  margin: 1rem 0 0;
  line-height: 1.65;
  font-weight: 400;
  text-align: center;
  max-width: min(52rem, 100%);
  font-family: 'Montserrat', sans-serif;
}

.splash-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  flex-wrap: nowrap;
  width: 100%;
  max-width: 620px;
}

.how-it-works-button,
.start-button {
  flex: 1 1 0;
  width: 100%;
  max-width: 300px;
  min-width: 210px;
  white-space: nowrap;
  appearance: none !important;
  -webkit-appearance: none !important;
  background-image: none !important;
  box-shadow: none !important;
  filter: none !important;
  opacity: 1 !important;
}

.how-it-works-button {
  background-color: var(--vt-c-accent) !important;
  color: var(--color-background) !important;
  border: 1px solid var(--vt-c-accent) !important;
  padding: var(--base-layout-button-padding-lg);
  border-radius: var(--base-layout-button-border-radius-lg);
  font-size: var(--base-layout-button-font-size-md);
  font-weight: var(--base-layout-button-font-weight);
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
  max-width: var(--base-layout-button-max-width);
  min-height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.how-it-works-button:hover {
  background-color: var(--ui-inverse-cta-bg-hover) !important;
  color: var(--color-background) !important;
}

.start-button {
  background-color: var(--vt-c-accent) !important;
  color: var(--color-background) !important;
  border: none !important;
  padding: var(--base-layout-button-padding-lg);
  border-radius: var(--base-layout-button-border-radius-lg);
  font-size: var(--base-layout-button-font-size-md);
  font-weight: var(--base-layout-button-font-weight);
  cursor: pointer;
  transition: background-color 0.2s ease;
  max-width: var(--base-layout-button-max-width);
  min-height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.start-button:hover {
  background-color: var(--ui-inverse-cta-bg-hover) !important;
}

.start-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: var(--vt-c-accent) !important;
  color: var(--color-background) !important;
  border: 1px solid var(--vt-c-accent) !important;
  box-shadow: none !important;
  filter: none !important;
}

.case-study-cta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.55rem;
}

.case-study-card {
  --case-study-size: clamp(80px, 11vw, 120px);
  display: block;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  appearance: none;
  width: var(--case-study-size);
  height: var(--case-study-size);
  aspect-ratio: 1 / 1;
  border-radius: 16px;
  overflow: hidden;
}

.case-study-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border: 1.5px solid rgba(var(--vt-c-accent-rgb), 0.35);
  display: block;
  transition: border-color 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
}

.case-study-card:hover .case-study-img {
  border-color: var(--vt-c-accent);
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(var(--vt-c-accent-rgb), 0.2);
}

.case-study-label-text {
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--vt-c-accent);
  line-height: 1.1;
  margin: 0;
}

/* Short desktops / laptops (MacBook ~800–900px tall) — tighten padding and
   title so the copy panel doesn't overflow the viewport and spill above the
   fixed header. Width floor keeps this off mobile, which has its own rules. */
@media (min-width: 769px) and (max-height: 900px) {
  .splash-copy-panel {
    padding: 1.1rem 1.4rem 1.25rem;
    border-radius: 1rem;
  }

  .title-main,
  .title-highlight {
    font-size: clamp(1.5rem, 2.4vw, 2.4rem);
    line-height: 1.1;
  }

  .splash-subtitle {
    font-size: clamp(0.95rem, 1.6vw, 1.15rem);
    margin-top: 0.5rem;
  }

  .splash-description {
    font-size: clamp(0.88rem, 1.4vw, 1rem);
    line-height: 1.45;
    margin-top: 0.65rem;
  }

  .splash-content {
    gap: clamp(0.75rem, 1.5vh, 1.25rem);
  }
}

@media (max-width: 768px) {
  .splash-section {
    height: var(--section-available-height);
    min-height: var(--section-available-height);
    max-height: var(--section-available-height);
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-gutter: stable;
    align-items: center;
    justify-content: center;
    padding: 0 !important;
    text-align: center;
  }

  .splash-content {
    flex-direction: column;
    height: auto;
    min-height: 100%;
    gap: clamp(1.25rem, 4vh, 2.5rem);
    padding: clamp(1.5rem, 5vh, 2.75rem) clamp(1rem, 4vw, 1.5rem);
  }

  .splash-left-content {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: auto;
  }

  .splash-copy-panel {
    max-width: min(42rem, 100%);
    padding: 1rem 1rem 1.15rem;
    margin-top: 0;
    border-radius: 1rem;
    background: var(--ui-inverse-surface);
    border: 2px solid rgba(24, 27, 47, 0.14);
    box-shadow:
      0 18px 40px rgba(7, 10, 20, 0.24),
      0 0 0 1px rgba(255, 255, 255, 0.16) inset;
  }

  .splash-model-container {
    display: none;
    width: 100%;
    height: clamp(6rem, 22vw, 11rem);
    min-height: 5rem;
    max-height: 12rem;
    --base-layout-splash-loading-image-size: 0.4;
  }

  .splash-cta {
    gap: 0.6rem;
    flex-wrap: nowrap;
  }

  .how-it-works-button,
  .start-button {
    flex: 1 1 0;
    font-size: clamp(1rem, 3.5vw, 1.25rem);
    padding: clamp(0.85rem, 3.5vw, 1.1rem) clamp(1.25rem, 5vw, 1.75rem);
    width: 100%;
    max-width: none;
    min-width: 0;
    border-radius: 12px;
  }

  .splash-title {
    font-size: clamp(1.6rem, 4vw, 2.6rem);
    width: 100%;
    text-align: center;
  }

  .title-main,
  .title-highlight {
    font-size: clamp(1.4rem, 4.5vw, 2.4rem);
    line-height: 1.2;
  }

  .title-subheading {
    font-size: clamp(1rem, 3.5vw, 1.6rem);
    margin-top: clamp(0.35rem, 2vw, 0.75rem);
    text-align: center;
    max-width: 100%;
  }

  .splash-subtitle {
    font-size: clamp(0.95rem, 3vw, 1.2rem);
    text-align: center;
    max-width: 100%;
    color: #ffffff;
  }

  .splash-description {
    font-size: clamp(0.9rem, 2.75vw, 1rem);
    line-height: 1.5;
    max-width: 100%;
    color: #ffffff;
  }

  .edge-top-left {
    --design-anchor-size: clamp(150px, 38vw, 280px);
    transform: translate(-38%, -32%);
  }

  .edge-top-center-left {
    --design-anchor-size: clamp(120px, 30vw, 220px);
    transform: translate(-50%, -42%) rotate(12deg);
  }

  .edge-top-center-right {
    --design-anchor-size: clamp(120px, 30vw, 220px);
    transform: translate(-50%, -40%) rotate(-8deg);
  }

  .edge-top-right {
    --design-anchor-size: clamp(150px, 38vw, 280px);
    transform: translate(34%, -30%) scaleX(-1);
  }

  .edge-right-mid {
    --design-anchor-size: clamp(130px, 34vw, 250px);
    transform: translate(40%, -50%) rotate(-15deg);
  }

  .edge-bottom-right {
    --design-anchor-size: clamp(150px, 38vw, 280px);
    transform: translate(34%, 36%) scaleY(-1);
  }

  .edge-bottom-left {
    --design-anchor-size: clamp(160px, 42vw, 300px);
    transform: translate(-36%, 38%) rotate(8deg);
  }

  .edge-left-mid {
    --design-anchor-size: clamp(130px, 34vw, 250px);
    transform: translate(-42%, -50%) rotate(10deg);
  }

}

@media (max-width: 480px) {
  .splash-copy-panel {
    padding: 0.9rem 0.85rem 1rem;
    border-radius: 0.9rem;
    background: var(--ui-inverse-surface);
  }

  .title-main,
  .title-highlight {
    font-size: clamp(1.3rem, 7vw, 1.8rem);
    line-height: 1.1;
  }

  .splash-subtitle {
    font-size: clamp(0.88rem, 4.3vw, 1rem);
    line-height: 1.2;
    color: #ffffff;
  }

  .splash-description {
    font-size: clamp(0.82rem, 3.8vw, 0.92rem);
    line-height: 1.45;
    margin-top: 0.7rem;
    color: #ffffff;
  }

  .splash-cta {
    gap: 0.5rem;
  }

  .how-it-works-button,
  .start-button {
    font-size: 0.95rem;
    padding: 0.82rem 0.95rem;
  }
}

</style>
